import { Injectable, inject } from '@angular/core';
import {
  HttpClient,
  HttpErrorResponse,
  HttpParams
} from '@angular/common/http';
import { Observable, catchError, map, throwError } from 'rxjs';
import { environment } from '@env/environment';
import { ExtractionSampleRepository } from '@service/extraction-sample/extraction-sample.repository';
import { ExtractionSample } from '@model/extraction-sample/extraction-sample.model';
import { PaginatedResponse } from '@model/common/pagination.model';
import { ExtractionSampleQueryParams } from '@model/extraction-sample/extraction-sample-query-params.model';
import { NotFoundException } from '@model/common/domain.exception';

/**
 * HTTP Repository Implementation for Extraction Samples
 * HU21-941147: Listar Órdenes por Extraer
 *
 * Connects to backend endpoints:
 * - GET /laboratorio/specimens/extraction (simple search + sorting)
 * - GET /laboratorio/specimens/extraction/filtered (advanced filters)
 */
@Injectable({
  providedIn: 'root'
})
export class HttpExtractionSampleRepository extends ExtractionSampleRepository {
  private readonly http = inject(HttpClient);
  private readonly BASE_URL = `${environment.laboratory.baseUrl}${environment.laboratory.context}/specimens/extraction`;
  private readonly DEFAULT_STATUS = 'PENDING_EXTRACTION';

  override findAll(
    params?: ExtractionSampleQueryParams
  ): Observable<PaginatedResponse<ExtractionSample>> {
    // Determine if we need simple search or advanced filters
    const hasAdvancedFilters =
      params?.examTypes ||
      params?.priorities ||
      params?.specimenTypes ||
      params?.dateStart ||
      params?.dateEnd ||
      params?.labelPrinted !== undefined;

    if (hasAdvancedFilters) {
      return this.findAllWithFilters(params);
    }

    // Simple search endpoint: GET /specimens/extraction
    let httpParams = new HttpParams()
      .set('status', this.DEFAULT_STATUS)
      .set('page', String(params?.page ?? 0))
      .set('size', String(params?.size ?? 10))
      .set('sortBy', params?.sortBy ?? 'labEntryDate')
      .set('sortDirection', params?.sortDirection ?? 'ASC');

    // Add search parameters if provided
    if (params?.searchField && params?.searchValue) {
      httpParams = httpParams
        .set('searchField', params.searchField)
        .set('searchValue', params.searchValue);
    }

    // Add labelPrinted filter if provided
    if (params?.labelPrinted !== undefined) {
      httpParams = httpParams.set('labelPrinted', String(params.labelPrinted));
    }

    return this.http
      .get<
        PaginatedResponse<ExtractionSample>
      >(this.BASE_URL, { params: httpParams })
      .pipe(
        catchError((error: HttpErrorResponse) => {
          if (error.status === 404) {
            return throwError(
              () =>
                new NotFoundException('EXTRACTION_SAMPLES_NOT_FOUND', {
                  filters: params
                })
            );
          }
          return throwError(() => error);
        })
      );
  }

  /**
   * Find all samples with advanced filters
   * Uses GET /specimens/extraction/filtered endpoint
   */
  private findAllWithFilters(
    params?: ExtractionSampleQueryParams
  ): Observable<PaginatedResponse<ExtractionSample>> {
    let httpParams = new HttpParams()
      .set('status', this.DEFAULT_STATUS)
      .set('page', String(params?.page ?? 0))
      .set('size', String(params?.size ?? 10))
      .set('sortBy', params?.sortBy ?? 'labEntryDate')
      .set('sortDirection', params?.sortDirection ?? 'ASC');

    if (params?.examTypes && params.examTypes.length > 0) {
      params.examTypes.forEach((type) => {
        httpParams = httpParams.append('examTypes', type);
      });
    }

    if (params?.priorities && params.priorities.length > 0) {
      params.priorities.forEach((priority) => {
        httpParams = httpParams.append('priorities', priority);
      });
    }

    if (params?.specimenTypes && params.specimenTypes.length > 0) {
      params.specimenTypes.forEach((type) => {
        httpParams = httpParams.append('specimenTypes', type);
      });
    }

    // Add date range filters
    if (params?.dateStart) {
      httpParams = httpParams.set('dateStart', params.dateStart);
    }

    if (params?.dateEnd) {
      httpParams = httpParams.set('dateEnd', params.dateEnd);
    }

    // Add labelPrinted filter if provided
    if (params?.labelPrinted !== undefined) {
      httpParams = httpParams.set('labelPrinted', String(params.labelPrinted));
    }

    return this.http
      .get<PaginatedResponse<ExtractionSample>>(`${this.BASE_URL}/filtered`, {
        params: httpParams
      })
      .pipe(
        catchError((error: HttpErrorResponse) => {
          if (error.status === 404) {
            return throwError(
              () =>
                new NotFoundException('EXTRACTION_SAMPLES_NOT_FOUND', {
                  filters: params
                })
            );
          }
          return throwError(() => error);
        })
      );
  }

  /**
   * Export specimens to CSV
   * @param params Query parameters including filters, search, and sorting
   * @returns Observable with Blob containing the CSV file
   */
  exportToCSV(params?: ExtractionSampleQueryParams): Observable<Blob> {
    const httpParams = this.buildExportParams(params);

    return this.http
      .get(`${this.BASE_URL}/export`, {
        params: httpParams,
        responseType: 'blob',
        observe: 'response'
      })
      .pipe(
        map((response) => {
          if (!response.body) {
            throw new NotFoundException('EXPORT_DATA_NOT_RECEIVED');
          }
          return response.body;
        }),
        catchError((error: HttpErrorResponse) => {
          return throwError(() => error);
        })
      );
  }

  /**
   * Build HTTP parameters for export endpoint
   * Centralizes parameter construction to avoid duplication
   */
  private buildExportParams(params?: ExtractionSampleQueryParams): HttpParams {
    let httpParams = new HttpParams()
      .set('status', this.DEFAULT_STATUS)
      .set('sortBy', params?.sortBy ?? 'labEntryDate')
      .set('sortDirection', params?.sortDirection ?? 'ASC');

    // Add search parameters if provided
    if (params?.searchField && params?.searchValue) {
      httpParams = httpParams
        .set('searchField', params.searchField)
        .set('searchValue', params.searchValue);
    }

    // Add advanced filter parameters if provided
    if (params?.examTypes && params.examTypes.length > 0) {
      params.examTypes.forEach((type) => {
        httpParams = httpParams.append('examTypes', type);
      });
    }

    if (params?.priorities && params.priorities.length > 0) {
      params.priorities.forEach((priority) => {
        httpParams = httpParams.append('priorities', priority);
      });
    }

    if (params?.specimenTypes && params.specimenTypes.length > 0) {
      params.specimenTypes.forEach((type) => {
        httpParams = httpParams.append('specimenTypes', type);
      });
    }

    if (params?.dateStart) {
      httpParams = httpParams.set('dateStart', params.dateStart);
    }

    if (params?.dateEnd) {
      httpParams = httpParams.set('dateEnd', params.dateEnd);
    }

    // Add labelPrinted filter if provided
    if (params?.labelPrinted !== undefined) {
      httpParams = httpParams.set('labelPrinted', String(params.labelPrinted));
    }

    return httpParams;
  }

  /**
   * Get empty response for error cases
   */
  private getEmptyResponse(): PaginatedResponse<ExtractionSample> {
    return {
      content: [],
      pageable: {
        page: 0,
        size: 10,
        totalElements: 0,
        totalPages: 0,
        first: true,
        last: true
      }
    };
  }

  /**
   * Print labels for selected specimen IDs
   * Sends POST request to backend with array of specimen UUIDs
   * Returns PDF blob for download
   * HU22-920838: Imprimir Etiqueta de Contenedores
   * @param specimenIds Array of specimen UUIDs to print
   * @returns Observable with PDF blob
   */
  override printLabels(specimenIds: string[]): Observable<Blob> {
    const endpoint = `${environment.laboratory.baseUrl}${environment.laboratory.context}/specimens/labels/print`;
    return this.http.post(
      endpoint,
      { specimenIds },
      {
        responseType: 'blob',
        headers: {
          Accept: 'application/pdf'
        }
      }
    );
  }
}
